import React from 'react';
import {useLiveQuery} from 'dexie-react-hooks';
import {db} from '../db/database';
import {Link,useNavigate} from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {format} from 'date-fns';
import {useUI} from '../context/UIContext';

const isPublishedStatus=status=> status==='Published' || status==='Sent';

function validateSowName(value) {
  const trimmed=value.trim();
  if (!trimmed) return 'SOW name is required.';
  if (trimmed.length < 3 || trimmed.length > 120) return 'Name must be 3 to 120 characters.';
  if (!/^[A-Za-z0-9 _-]+$/.test(trimmed)) return 'Use letters, numbers, spaces, hyphens or underscores only.';
  if (!/[A-Za-z0-9]/.test(trimmed)) return 'Name must contain at least one letter or number.';
  return '';
}

function calculateTotal(proposal,settings) {
  const items=proposal?.financials?.items || [];
  const subtotal=items.reduce((sum,item)=> sum + (Number(item.amount) || 0),0);
  const taxRate=settings?.taxRate || 10;
  const taxEnabled=proposal?.financials?.taxEnabled;
  const taxAmount=taxEnabled ? subtotal * (taxRate / 100) : 0;
  return subtotal + taxAmount;
}

function calculateDeposit(proposal,total) {
  const milestones=proposal?.paymentMilestones || [];
  const depositMilestone=milestones.find(m=>
    m.label && m.label.toLowerCase().includes('deposit') && (Number(m.percent) || 0) > 0
  );
  if (!depositMilestone) return null;
  const percent=Number(depositMilestone.percent) || 0;
  return {amount: total * percent / 100,percent};
}

function formatCurrency(amount,currency) {
  const validatedCurrency=currency || 'AUD';
  try {
    return new Intl.NumberFormat('en-AU',{style: 'currency',currency: validatedCurrency}).format(amount || 0);
  } catch (e) {
    return new Intl.NumberFormat('en-AU',{style: 'currency',currency: 'AUD'}).format(amount || 0);
  }
}

function normaliseTelHref(phone) {
  if (!phone || typeof phone !=='string') return '';
  let cleaned=phone.replace(/[\s()\-\u2010\u2011\u2012\u2013\u2014]/g,'');
  if (!cleaned) return '';
  if (cleaned.startsWith('+61')) {
    if (/^\+61\d+$/.test(cleaned)) return cleaned;
    return '';
  }
  if (cleaned.startsWith('04')) {
    const rest=cleaned.slice(2);
    if (/^\d+$/.test(rest)) return '+614' + rest;
    return '';
  }
  if (cleaned.startsWith('614')) {
    const rest=cleaned.slice(3);
    if (/^\d+$/.test(rest)) return '+614' + rest;
    return '';
  }
  return '';
}

function getDisplayPhone(proposal) {
  return proposal.clientMobile || proposal.mobileNumber || proposal.phoneNumber || proposal.clientPhone || '';
}

const APPROVAL_STATUS_MAP={
  PENDING: {label: 'Awaiting Approval',className: 'bg-amber-50 text-amber-700 border-amber-200'},
  APPROVED: {label: 'Approved',className: 'bg-green-50 text-green-700 border-green-200'},
  CHANGES_REQUESTED: {label: 'Changes Requested',className: 'bg-blue-50 text-blue-700 border-blue-200'},
  EXPIRED: {label: 'Expired',className: 'bg-orange-50 text-orange-700 border-orange-200'},
  REVOKED: {label: 'Revoked',className: 'bg-red-50 text-red-700 border-red-200'},
  SEND_FAILED: {label: 'Send Failed',className: 'bg-red-50 text-red-700 border-red-200'},
};

export default function Dashboard() {
  const navigate=useNavigate();
  const {showToast,promptText}=useUI();
  const proposals=useLiveQuery(()=> db.proposals.orderBy('updatedAt').reverse().toArray()) || [];
  const snippets=useLiveQuery(()=> db.snippets.count()) || 0;
  const settings=useLiveQuery(()=> db.settings.get('global')) || {};

  const draftCount=proposals.filter(p=> !p.status || p.status==='Draft').length;
  const sentCount=proposals.filter(p=> isPublishedStatus(p.status)).length;

  const handleCreateNew=async ()=> {
    const name=await promptText({
      title: 'Name your Statement of Work',
      message: 'Enter a clear internal and client-facing name for this SOW.',
      label: 'SOW name',
      placeholder: 'ASG-Operations_Job-Costing',
      helperText: 'Letters, numbers, spaces, hyphens and underscores only.',
      confirmText: 'Create SOW',
      cancelText: 'Cancel',
      maxLength: 120,
      validate: validateSowName,
    });
    if (!name) return;
    navigate('/proposals/new',{state: {initialSowTitle: name}});
  };

  const handleDuplicate=async (e,prop)=> {
    e.preventDefault();
    e.stopPropagation();
    const newId=crypto.randomUUID();
    const {
      documentNumber,documentNumberAssignedAt,documentVersion,lastProfessionalPdfAt,
      lastProfessionalPdfFileName,googleDocFileId,pdfFileId,googleDocUrl,pdfDriveUrl,
      generationId,publishedAt,reopenedAt,version,...rest
    }=prop;
    const duplicatedProposal={
      ...rest,
      id: newId,
      title: `${prop.title || 'Untitled SOW'} (Copy)`,
      status: 'Draft',
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    await db.proposals.add(duplicatedProposal);
    showToast("Proposal Duplicated");
    navigate(`/proposals/${newId}`);
  };

  return (
    <div className="p-4 sm:p-6 xl:p-7 mx-auto w-full" style={{maxWidth: '1440px'}}>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 sm:mb-8 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">Manage your professional Statements of Work.</p>
        </div>
        <button onClick={handleCreateNew} className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-lg font-medium shadow-sm transition-all transform hover:scale-[1.02] sm:self-end">
          <SafeIcon icon={FiIcons.FiPlus} /> New SOW
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 mb-8 sm:mb-10">
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm flex items-center gap-4" title="SOWs currently in Draft status.">
          <div className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xl">
            <SafeIcon icon={FiIcons.FiFileText} />
          </div>
          <div>
            <div className="text-[22px] font-bold text-gray-900">{draftCount}</div>
            <div className="text-sm font-medium text-gray-500 uppercase tracking-wide">Active Drafts</div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm flex items-center gap-4" title="SOWs with Published or legacy Sent status.">
          <div className="w-11 h-11 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-xl">
            <SafeIcon icon={FiIcons.FiSend} />
          </div>
          <div>
            <div className="text-[22px] font-bold text-gray-900">{sentCount}</div>
            <div className="text-sm font-medium text-gray-500 uppercase tracking-wide">Published</div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm flex items-center gap-4" title="Reusable content snippets, not SOW documents.">
          <div className="w-11 h-11 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 text-xl">
            <SafeIcon icon={FiIcons.FiBookOpen} />
          </div>
          <div>
            <div className="text-[22px] font-bold text-gray-900">{snippets}</div>
            <div className="text-sm font-medium text-gray-500 uppercase tracking-wide">Snippet Library Items</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Recent Documents</h2>
            <p className="text-xs text-gray-400 mt-0.5">Latest five SOWs by last modification date.</p>
          </div>
          <Link to="/proposals" className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">View All Library</Link>
        </div>
        <div className="divide-y divide-gray-200">
          {proposals.length===0 ? (
            <div className="p-12 text-center text-gray-400 italic">
              No documents created yet. Start by creating a new SOW.
            </div>
          ) : (
            proposals.slice(0,5).map(prop=> {
              const total=calculateTotal(prop,settings);
              const deposit=calculateDeposit(prop,total);
              const currency=settings?.currency || 'AUD';
              const phoneDisplay=getDisplayPhone(prop);
              const phoneHref=normaliseTelHref(phoneDisplay);
              const approvalCfg=prop.approval?.status ? APPROVAL_STATUS_MAP[prop.approval.status] : null;
              return (
                <div key={prop.id} className="p-4 sm:p-5 hover:bg-gray-50 transition-colors">
                  <div className="grid grid-cols-1 xl:grid-cols-12 gap-3 xl:gap-4 items-start xl:items-center">
                    <div className="xl:col-span-4 min-w-0">
                      <Link to={`/proposals/${prop.id}`} className="text-base font-medium text-indigo-600 hover:underline block truncate">
                        {prop.title || 'Untitled SOW'}
                      </Link>
                      <div className="flex items-center gap-1 mt-1 text-sm text-gray-500">
                        <SafeIcon icon={FiIcons.FiUser} /> {prop.clientName || 'No Client'}
                      </div>
                      <div className="flex items-center gap-1 mt-0.5 text-sm text-gray-500">
                        <SafeIcon icon={FiIcons.FiClock} /> {format(new Date(prop.updatedAt),'MMM d,yyyy')}
                      </div>
                    </div>
                    <div className="xl:col-span-3 min-w-0 space-y-0.5">
                      {prop.sections?.pointOfContact && (
                        <div className="text-sm text-gray-600 font-medium truncate">{prop.sections.pointOfContact}</div>
                      )}
                      {prop.emailAddress && (
                        <a href={`mailto:${prop.emailAddress}`} className="block text-sm text-indigo-600 hover:underline truncate">
                          {prop.emailAddress}
                        </a>
                      )}
                      {phoneDisplay && phoneHref && (
                        <a href={`tel:${phoneHref}`} className="block text-sm text-indigo-600 hover:underline">
                          {phoneDisplay}
                        </a>
                      )}
                    </div>
                    <div className="xl:col-span-2 min-w-0">
                      {total > 0 ? (
                        <>
                          <div className="text-sm font-bold text-gray-900">{formatCurrency(total,currency)}</div>
                          {deposit && (
                            <div className="text-xs text-gray-500 mt-0.5">
                              Deposit {deposit.percent}% · {formatCurrency(deposit.amount,currency)}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-sm text-gray-400 italic">No commercial value entered</div>
                      )}
                    </div>
                    <div className="xl:col-span-3 flex items-center gap-2 flex-wrap">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${isPublishedStatus(prop.status) ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {isPublishedStatus(prop.status) ? 'Published' : (prop.status || 'Draft')}
                      </span>
                      {approvalCfg && (
                        <span className={`px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider border ${approvalCfg.className}`}>
                          {approvalCfg.label}
                        </span>
                      )}
                      <div className="flex items-center gap-2 ml-auto">
                        <button onClick={(e)=> handleDuplicate(e,prop)} className="p-2 text-gray-400 hover:text-indigo-600 bg-white rounded-lg border border-gray-200 hover:border-indigo-200 shadow-sm transition-all" title="Duplicate" style={{minWidth: '36px',minHeight: '36px'}}>
                          <SafeIcon icon={FiIcons.FiCopy} />
                        </button>
                        <Link to={`/proposals/${prop.id}`} className="p-2 text-gray-400 hover:text-indigo-600 bg-white rounded-lg border border-gray-200 hover:border-indigo-200 shadow-sm" title="Edit" style={{minWidth: '36px',minHeight: '36px'}}>
                          <SafeIcon icon={FiIcons.FiEdit2} />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}